/**
 * OfferStatusBadge - Shows per-offer processing status
 *
 * Displays: Available (green), Processing (blue), Rejected (red with lock + reason),
 * Approved (green), Disbursed (green)
 *
 * For rejected offers, shows the full rejection reason visible to ALL employees.
 */

import React from 'react';
import { Tag } from 'primereact/tag';
import { getOfferStatusConfig, getReasonLabel } from '../../services/leadProcessingService';

const OfferStatusBadge = ({ offerStatus, compact = false }) => {
    if (!offerStatus) return null;

    const status = offerStatus.status || 'available';
    const config = getOfferStatusConfig(status);

    // Compact mode: just the tag
    if (compact || status === 'available') {
        return (
            <Tag
                value={config.label}
                severity={config.severity}
                className="text-xs"
                icon={`pi ${config.icon}`}
            />
        );
    }

    // In processing
    if (status === 'in_processing' || status === 'processing') {
        return (
            <div className="border-round p-2" style={{ backgroundColor: 'var(--blue-50)' }}>
                <Tag value={config.label} severity={config.severity} className="text-xs" icon={`pi ${config.icon}`} />
                <div className="text-blue-600 text-xs mt-1">Being processed by another employee</div>
            </div>
        );
    }

    // Rejected — full display with reason (differentiate bank vs processor vs customer)
    const rejectionStatuses = ['rejected', 'bank_rejected', 'customer_rejected', 'processor_rejected'];
    if (rejectionStatuses.includes(status)) {
        const isProcessorRejection = status === 'processor_rejected' || offerStatus.rejection_type === 'processor';
        const isCustomerRejection = status === 'customer_rejected' || offerStatus.rejection_type === 'customer';
        const bgColor = isProcessorRejection ? 'var(--orange-50)' : 'var(--red-50)';
        const borderColor = isProcessorRejection ? 'var(--orange-200)' : 'var(--red-200)';
        const tagLabel = isProcessorRejection ? 'PROCESSOR REJECTED' : isCustomerRejection ? 'CUSTOMER REJECTED' : 'BANK REJECTED';
        const tagSeverity = isProcessorRejection ? 'warning' : 'danger';

        return (
            <div className="border-round p-2 border-1" style={{ backgroundColor: bgColor, borderColor: borderColor }}>
                <div className="flex align-items-center gap-2 mb-1 flex-wrap">
                    <i className={`pi ${isProcessorRejection ? 'pi-user-minus text-orange-500' : isCustomerRejection ? 'pi-user-minus text-red-500' : 'pi-lock text-red-500'}`} />
                    <Tag value={tagLabel} severity={tagSeverity} className="text-xs" />
                    {offerStatus.rejected_by_name && (
                        <span className="text-500 text-xs">by <strong>{offerStatus.rejected_by_name}</strong></span>
                    )}
                </div>
                {offerStatus.rejection_reason_code && (
                    <div className="text-red-700 text-xs font-semibold mt-1">
                        {offerStatus.rejection_reason_label || getReasonLabel(offerStatus.rejection_reason_code)}
                    </div>
                )}
                {offerStatus.rejection_detail && (
                    <div className="text-red-600 text-xs font-italic mt-1" style={{ lineHeight: '1.3' }}>
                        "{offerStatus.rejection_detail}"
                    </div>
                )}
                {offerStatus.rejected_at && (
                    <div className="text-400 text-xs mt-1">
                        {new Date(offerStatus.rejected_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </div>
                )}
            </div>
        );
    }

    // Approved / Disbursed
    return (
        <div className="border-round p-2" style={{ backgroundColor: 'var(--green-50)' }}>
            <Tag value={config.label} severity={config.severity} className="text-xs" icon={`pi ${config.icon}`} />
        </div>
    );
};

export default OfferStatusBadge;
